import React, { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useFocusTrap } from '../../hooks/use-focus-trap';
import { createCheckoutSession, PlanType } from '../../services/paymentService';
import './UpgradeModal.scss';
import { uiLocale } from '../../i18n/language';

// Backend caps a checkout at 50 seats (CreateCheckoutSessionRequest.seat_count). The buyer
// takes one of them, so the teacher picker tops out one lower.
const MAX_SCHOOL_TEACHERS = 49;

interface UpgradeModalProps {
  onClose?: () => void;
}

const TEACHER_PRICE_MONTHLY = 15;
const TEACHER_PRICE_ANNUAL = 9;
const SCHOOL_PRICE_PER_SEAT_MONTHLY = 15;
const SCHOOL_PRICE_PER_SEAT_ANNUAL = 9;

const UpgradeModal: React.FC<UpgradeModalProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { trialSessionsUsed, trialSessionLimit, currentUser, token } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState<'teacher' | 'school' | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [schoolSeatsInput, setSchoolSeatsInput] = useState('1');
  const schoolSeats = Math.min(MAX_SCHOOL_TEACHERS, Math.max(1, parseInt(schoolSeatsInput, 10) || 1));
  const seatInputId = useId();
  const seatPreviewId = useId();
  const modalRef = useRef<HTMLDivElement>(null);
  // Mounted only while open. SideBar also closes this on Escape; a second onClose call is a
  // no-op (it only sets the parent's "open" state to false).
  useFocusTrap(modalRef, true, { onEscape: onClose });

  const trialEndDate = currentUser?.access_end_date
    ? new Date(currentUser.access_end_date).toLocaleDateString(uiLocale(), {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const isDateExpired =
    currentUser?.access_end_date && new Date() > new Date(currentUser.access_end_date);
  const isLimitReached = trialSessionsUsed >= trialSessionLimit;
  const isTrialEnded = isDateExpired || isLimitReached;

  const teacherPrice = isAnnual ? TEACHER_PRICE_ANNUAL : TEACHER_PRICE_MONTHLY;
  const schoolPricePerSeat = isAnnual ? SCHOOL_PRICE_PER_SEAT_ANNUAL : SCHOOL_PRICE_PER_SEAT_MONTHLY;
  // +1 for the buyer themselves; schoolSeats is the number of invited teachers
  const schoolTotalSeats = schoolSeats + 1;
  const schoolTotal = schoolTotalSeats * schoolPricePerSeat;
  const teacherAnnualTotal = teacherPrice * 12;
  const schoolAnnualTotal = schoolTotal * 12;

  const handleTeacherPlan = async (planType: PlanType) => {
    if (!token) return;
    setCheckoutError(null);
    setCheckoutLoading('teacher');
    try {
      const { checkout_url } = await createCheckoutSession(token, planType);
      window.location.href = checkout_url;
    } catch (err: any) {
      setCheckoutError(err.message ?? t('common.somethingWentWrongTryAgain'));
      setCheckoutLoading(null);
    }
  };

  const handleSchoolPlan = async (planType: PlanType) => {
    if (!token) return;
    setCheckoutError(null);
    setCheckoutLoading('school');
    try {
      const { checkout_url } = await createCheckoutSession(token, planType, schoolTotalSeats);
      window.location.href = checkout_url;
    } catch (err: any) {
      setCheckoutError(err.message ?? t('common.somethingWentWrongTryAgain'));
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="upgrade-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="upgrade-modal-title">
      <div className="upgrade-modal" ref={modalRef}>

        {onClose && (
          <button className="upgrade-modal__close" onClick={onClose} aria-label={t('common.close')}>✕</button>
        )}

        {/* Header */}
        <div className="upgrade-modal__header">
          <div className="upgrade-modal__top-badge">{t('common.realityCoachPricing')}</div>
          <h2 id="upgrade-modal-title" className="upgrade-modal__title">
            {isTrialEnded
              ? (currentUser?.is_trial ? t('subscription.freeTrialEnded') : t('subscription.subscriptionExpired'))
              : t('common.chooseOptionBestFitsSchool')}
          </h2>
          <p className="upgrade-modal__subtitle">
            {isTrialEnded
              ? (currentUser?.is_trial
                  ? (isLimitReached
                      ? t('subscription.trialSessionsUsedUp', { trialSessionLimit })
                      : t('subscription.trialExpiredOn', { trialEndDate }))
                  : t('subscription.subscriptionExpiredOn', { trialEndDate }))
              : t('common.comparePlansSubtitle')}
          </p>

          {/* Billing toggle */}
          <div className="upgrade-modal__toggle">
            <span className={!isAnnual ? 'upgrade-modal__toggle-label--active' : 'upgrade-modal__toggle-label'}>
              {t('common.monthlyView')}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={isAnnual}
              className={`upgrade-modal__toggle-btn${isAnnual ? ' upgrade-modal__toggle-btn--on' : ''}`}
              onClick={() => setIsAnnual(v => !v)}
              aria-label={t('common.annualBilling')}
            >
              <span className="upgrade-modal__toggle-knob" />
            </button>
            <span className={isAnnual ? 'upgrade-modal__toggle-label--active' : 'upgrade-modal__toggle-label'}>
              {t('common.annualView')}
            </span>
            {isAnnual && <span className="upgrade-modal__discount-badge">{t('common.fortyPercentDiscount')}</span>}
          </div>
        </div>

        {/* Pricing cards */}
        <div className="upgrade-modal__cards">

          {/* Google Marketplace */}
          <div className="upgrade-modal__card">
            <h3 className="upgrade-modal__card-name">Google Marketplace</h3>
            <div className="upgrade-modal__card-price">
              <span className="upgrade-modal__price-amount upgrade-modal__price-amount--talk">{t('subscription.gcpBilling')}</span>
            </div>
            <div className="upgrade-modal__card-billing">
              {t('subscription.viaExistingGcpAccount')}
            </div>
            <a
              className="upgrade-modal__card-cta upgrade-modal__card-cta--outline"
              href="https://console.cloud.google.com/marketplace/product/da-realitycoach-prod/reality-coach"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('subscription.continueWithGoogleMarketplace')}
            </a>
            <p className="upgrade-modal__card-tagline">
              {t('subscription.subscribeViaGcp')}
            </p>
            <hr className="upgrade-modal__card-divider" />
            <ul className="upgrade-modal__card-features">
              <li>{t('subscription.gcpBillingAccount')}</li>
              <li>{t('common.fullPlatformAccess')}</li>
              <li>{t('subscription.managedViaCloudConsole')}</li>
              <li>{t('subscription.usageBasedBilling')}</li>
            </ul>
          </div>

          {/* Teacher */}
          <div className="upgrade-modal__card">
            <h3 className="upgrade-modal__card-name">{t('common.teacher')}</h3>
            <div className="upgrade-modal__card-price">
              <span className="upgrade-modal__price-amount">${isAnnual ? teacherAnnualTotal : teacherPrice}</span>
            </div>
            <div className="upgrade-modal__card-billing">
              {isAnnual ? t('subscription.perTeacherYearly') : t('subscription.perTeacherMonthly')}
            </div>
            {isAnnual && (
              <p className="upgrade-modal__price-subtext">
                {t('subscription.pricePerMonthAndYear', { monthly: teacherPrice, yearly: teacherAnnualTotal })}
              </p>
            )}
            <button
              className="upgrade-modal__card-cta upgrade-modal__card-cta--outline"
              onClick={() => handleTeacherPlan(isAnnual ? 'yearly' : 'monthly')}
              disabled={checkoutLoading !== null}
            >
              {checkoutLoading === 'teacher' ? t('subscription.redirecting') : t('common.chooseTeacher')}
            </button>
            <p className="upgrade-modal__card-tagline">
              {t('common.teacherPlanTagline')}
            </p>
            <hr className="upgrade-modal__card-divider" />
            <ul className="upgrade-modal__card-features">
              <li>{t('common.unlimitedRoleplays')}</li>
              <li>{t('common.postSessionFeedback')}</li>
              <li>{t('common.personalAnalyticsDashboard')}</li>
              <li>{t('common.badgesStreaksMilestones')}</li>
            </ul>
          </div>

          {/* School — Recommended */}
          <div className="upgrade-modal__card upgrade-modal__card--recommended">
            <div className="upgrade-modal__recommended-tag">{t('subscription.recommended')}</div>
            <h3 className="upgrade-modal__card-name">{t('common.school')}</h3>
            <div className="upgrade-modal__card-price">
              <span className="upgrade-modal__price-amount">${isAnnual ? schoolAnnualTotal : schoolTotal}</span>
            </div>
            <div className="upgrade-modal__card-billing">
              {t(isAnnual ? 'subscription.schoolSeatsYearly' : 'subscription.schoolSeatsMonthly', { total: schoolTotalSeats, count: schoolSeats })}
            </div>
            {isAnnual && (
              <p className="upgrade-modal__price-subtext">
                {t('subscription.pricePerMonthAndYear', { monthly: schoolTotal, yearly: schoolAnnualTotal })}
              </p>
            )}
            <button
              className="upgrade-modal__card-cta upgrade-modal__card-cta--filled"
              onClick={() => handleSchoolPlan(isAnnual ? 'yearly' : 'monthly')}
              disabled={checkoutLoading !== null}
            >
              {checkoutLoading === 'school' ? t('subscription.redirecting') : t('common.chooseSchool')}
            </button>
            <p className="upgrade-modal__card-tagline">
              {t('subscription.schoolPlanTagline')}
            </p>
            <div className="upgrade-modal__seat-selector">
              <label className="upgrade-modal__seat-label" htmlFor={seatInputId}>{t('subscription.teachers')}</label>
              <input
                id={seatInputId}
                aria-describedby={seatPreviewId}
                type="number"
                className="upgrade-modal__seat-input"
                value={schoolSeatsInput}
                min={1}
                max={MAX_SCHOOL_TEACHERS}
                onChange={e => { const v = e.target.value; if (v === '' || /^\d+$/.test(v)) setSchoolSeatsInput(v); }}
                onBlur={() => setSchoolSeatsInput(String(schoolSeats))}
              />
              <p className="upgrade-modal__seat-preview" id={seatPreviewId}>
                {t('subscription.schoolSeatsTotal', { total: schoolTotalSeats, count: schoolSeats, price: schoolPricePerSeat })}
              </p>
            </div>
            <hr className="upgrade-modal__card-divider" />
            <ul className="upgrade-modal__card-features">
              <li>{t('common.everythingInTeacher')}</li>
              <li>{t('common.schoolDashboardsReporting')}</li>
              <li>{t('common.userManagementRoleBased')}</li>
              <li>{t('common.orgLevelAnalytics')}</li>
            </ul>
          </div>

          {/* District */}
          <div className="upgrade-modal__card">
            <h3 className="upgrade-modal__card-name">{t('common.district')}</h3>
            <div className="upgrade-modal__card-price">
              <span className="upgrade-modal__price-amount upgrade-modal__price-amount--talk">{t('common.letsTalk')}</span>
            </div>
            <div className="upgrade-modal__card-billing">
              {t('common.customQuote')}<br />{t('common.districtPurchasingRollout')}
            </div>
            <a
              className="upgrade-modal__card-cta upgrade-modal__card-cta--outline"
              href="mailto:sales@realitycoach.ai"
            >
              {t('common.contactSales')}
            </a>
            <p className="upgrade-modal__card-tagline">
              {t('common.districtPlanTagline')}
            </p>
            <hr className="upgrade-modal__card-divider" />
            <ul className="upgrade-modal__card-features">
              <li>{t('common.everythingInTeacherAndSchool')}</li>
              <li>{t('common.districtDashboardsReporting')}</li>
              <li>{t('common.administratorTools')}</li>
              <li>{t('common.customQuoteThroughSales')}</li>
            </ul>
          </div>

        </div>

        {checkoutError && (
          <p className="upgrade-modal__error" role="alert">{checkoutError}</p>
        )}

        {onClose && !isTrialEnded && (
          <div className="upgrade-modal__footer">
            <button className="upgrade-modal__dismiss" onClick={onClose}>
              {t('subscription.decideLater')}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default UpgradeModal;
